import { answerMatchesTargets } from '@/lib/answerShapes';

/**
 * Conditional question gating for the profile wizard (REK-1916).
 *
 * A question can declare a dependency on another question's answer via two
 * columns on `question_catalog`:
 *   - `depends_on_question_id` — the PARENT question.
 *   - `depends_on_values`      — the parent answer values that ENABLE (show) this
 *                                question.
 *
 * A dependent question is shown ONLY when the parent's stored answer is one of
 * `depends_on_values`; if the parent is unanswered, the dependent is hidden.
 * Canonical use case: child-specific questions ("Do you vaccinate your
 * children?") gate on "Do you have children?" so they are not asked of users
 * who indicated they have no children.
 *
 * Both surfaces that must agree on the "questions asked" set apply this:
 *   - the wizard (`ProfileWizardContext` display + navigation), and
 *   - the completion-% denominator (`useProfileProgress`).
 * Keeping them identical (alongside `dedupeProfileQuestions`) is what lets
 * "answered everything you were shown" reach 100% (REK-1869).
 *
 * Answer values are compared in the wizard's derived option-id form. The wizard
 * derives an option id as `option.text.toLowerCase().replace(/\s+/g, '_')` —
 * i.e. lowercase, with runs of WHITESPACE replaced by single underscores; all
 * other characters are preserved (notably hyphens stay, so "Children 6-12"
 * becomes `children_6-12`, NOT `children_6_12`). The data migration writes
 * `depends_on_values` in that exact form, and these tests lock the mapping.
 */

export interface GatableQuestion {
  depends_on_question_id?: string | null;
  depends_on_values?: string[] | null;
}

/** A stored profile answer: a single selection id or (for multi-select) a list. */
export type ProfileAnswerValue = string | string[] | null | undefined;

/**
 * Returns true if `dependency` is satisfied given a lookup of answers by
 * question id. A question with no dependency is always satisfied.
 *
 * @param dependsOnQuestionId parent question id (null/undefined = no gate)
 * @param dependsOnValues     parent answer values that enable the dependent
 * @param getAnswer           resolves a question id to its stored selection
 * @param normalizeMatching   flag OFF (default — main behaviour): exact
 *                            membership only. Flag ON: exact, then
 *                            `__N`-stripped exact, then
 *                            case/whitespace-normalised label equality.
 */
export function isDependencySatisfied(
  dependsOnQuestionId: string | null | undefined,
  dependsOnValues: string[] | null | undefined,
  getAnswer: (questionId: string) => ProfileAnswerValue,
  normalizeMatching = false,
): boolean {
  // No parent declared -> unconditional.
  if (!dependsOnQuestionId) return true;

  // Misconfigured (parent but no enabling values) -> do NOT hide. Hiding a
  // question with no way to ever satisfy it would make completion unreachable;
  // failing open is the safer default.
  const required = dependsOnValues ?? [];
  if (required.length === 0) return true;

  const parentAnswer = getAnswer(dependsOnQuestionId);
  // Parent unanswered -> dependent hidden until the parent is answered.
  if (parentAnswer === null || parentAnswer === undefined) return false;

  // Flag OFF: byte-identical to main — exact membership only.
  if (!normalizeMatching) {
    const answers = Array.isArray(parentAnswer) ? parentAnswer : [parentAnswer];
    return answers.some((a) => required.includes(a));
  }
  // Flag ON: dependency-rule matching (shared) — exact, then
  // `__N`-stripped exact, then case/whitespace-normalised label equality.
  return answerMatchesTargets(parentAnswer, required);
}

/**
 * Filter a list of questions down to the ones currently eligible to show, given
 * the user's answers. Preserves input order.
 */
export function applyProfileGating<T extends GatableQuestion>(
  questions: ReadonlyArray<T>,
  getAnswer: (questionId: string) => ProfileAnswerValue,
  /**
   * Flag OFF (default — main behaviour): exact membership. Flag ON:
   * normalized label equality may also satisfy.
   */
  normalizeMatching = false,
): T[] {
  return questions.filter((q) =>
    isDependencySatisfied(q.depends_on_question_id, q.depends_on_values, getAnswer, normalizeMatching),
  );
}
